// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { Component } from "react";

import { Field } from "formik";
import PropTypes from "prop-types";

import { Button, FileButton, ButtonsWrapper, Link } from "components/elements";
import { Label, ValidationBadge } from "components/form";
import { initialFile } from "components/form/fields/UploaderField";
import { Column, Row, GrabsonIcon } from "components/layout";
import { trans } from "src/translations";
import { isFormatSupported, validateImage } from "utils/image";
import { createFieldValidator, isRequired } from "utils/validation";

import { STATES } from "./constants";
import { Progressbar } from "./Progressbar";
import styles from "./UploaderField.scss";

/**
 * @typedef {Object} ImageValidationMetadata
 * @property {number} minWidth - Minimum width in pixels
 * @property {number} minHeight - Minimum height in pixels
 * @property {number} [maxWidth] - Maximum width in pixels (optional)
 * @property {number} [maxHeight] - Maximum height in pixels (optional)
 * @property {number} maxSize - Maximum file size in bytes
 * @property {boolean} [validateAspectRatio] - Whether to validate aspect ratio (optional)
 */

/**
 * @typedef {"no-preview" | "simple" | "big"} UploaderLook
 */

/**
 * @typedef {Object} UploaderFieldProps
 * @property {string} name - Field name (required)
 * @property {string} [label] - Uploader name displayed as a header
 * @property {string} [info] - Additional information displayed in header's label
 * @property {string[]} accept - Array of MIME types, e.g. ["image/jpeg"] (required)
 * @property {string} [className] - CSS class name
 * @property {boolean} [disabled] - Whether the uploader is disabled
 * @property {boolean} [required] - Whether the field is required
 * @property {ImageValidationMetadata} [imageValidationMetadata] - Image validation configuration
 * @property {(blobUrl: string, type: string) => Promise<string>} [preprocess] - Preprocessing function for uploaded files
 * @property {() => void} [onRemove] - Callback when file is removed
 * @property {() => void} [onClick] - Callback when clicked
 * @property {() => void} [onSuccess] - Callback when upload succeeds
 * @property {string} [dataTestId] - Test ID for testing
 * @property {string} [tooltip] - Tooltip text
 * @property {UploaderLook} [look] - Visual appearance of image preview
 */

/**
 * `<Uploader>` component allows to upload file inside form.
 * It has build in configurable validation and preprocessing.
 * @extends {Component<UploaderFieldProps>}
 */
export class UploaderField extends Component {
  static propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string, // Uploader name displayed as a header
    info: PropTypes.string, // Additional information displayed in header's label
    accept: PropTypes.arrayOf(PropTypes.string).isRequired, // Array of MIME types, e.g. ["image/jpeg"]
    className: PropTypes.string,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    imageValidationMetadata: PropTypes.shape({
      minWidth: PropTypes.number.isRequired,
      minHeight: PropTypes.number.isRequired,
      maxWidth: PropTypes.number,
      maxHeight: PropTypes.number,
      maxSize: PropTypes.number.isRequired,
      validateAspectRatio: PropTypes.bool,
    }),
    preprocess: PropTypes.func,
    onRemove: PropTypes.func,
    onClick: PropTypes.func,
    onSuccess: PropTypes.func,
    dataTestId: PropTypes.string,
    tooltip: PropTypes.string,
    look: PropTypes.oneOfType(["no-preview", "simple", "big"]),
  };

  state = {
    error: undefined,
  };

  static defaultProps = {
    required: true,
    dataTestId: "uploader",
    look: "simple",
  };

  componentDidUpdate(prevProps) {
    if (this.props.name !== prevProps.name) {
      this.setState({ error: undefined });
    }
  }

  getInitialImgFile = () => initialFile("");

  uploadFile = (setFieldValue, setFieldTouched) => async (ev) => {
    const { name, accept, preprocess, onSuccess } = this.props;
    const [file] = ev.target.files;
    const fieldName = `${name}`;
    const urlFieldName = `${name}.url`;
    const initialImgFile = this.getInitialImgFile();
    let blobUrl = "";

    if (!file) {
      return;
    }
    this.setState({ error: undefined });
    setFieldValue(fieldName, {
      ...initialImgFile,
      status: { ...STATES.progress },
    });

    try {
      if (!isFormatSupported(file, accept)) {
        throw trans.INVALID_IMAGE_FORMAT({
          formats: accept.join(", ").replace(/image\//gi, ""),
        });
      }

      const type = file.type;
      blobUrl = URL.createObjectURL(file);

      if (this.props.imageValidationMetadata) {
        await validateImage(blobUrl, this.props.imageValidationMetadata);
      }
      if (preprocess) {
        blobUrl = await preprocess(blobUrl, type);
      }
    } catch (err) {
      setFieldValue(fieldName, {
        ...initialImgFile,
      });
      this.setState({ error: err });
      setFieldTouched(urlFieldName);
      return;
    }
    setFieldTouched(urlFieldName, true, false);
    setFieldValue(fieldName, {
      ...initialImgFile,
      url: blobUrl,
      loaded: 100,
      status: { ...STATES.uploaded },
    });

    if (onSuccess) {
      onSuccess();
    }
  };

  removeFile = (url, setFieldValue) => {
    const { onRemove, name } = this.props;

    URL.revokeObjectURL(url);
    setFieldValue(name, this.getInitialImgFile());

    if (onRemove) {
      onRemove();
    }
  };

  progress({ url, status, loaded }, setFieldValue) {
    const { disabled, onClick, dataTestId } = this.props;

    const progressClasses = [];
    progressClasses.push(styles.progress);
    if (status?.progress) {
      progressClasses.push(styles.inactive);
    }

    const icon = onClick ? (
      <Button
        className={styles.leftButton}
        onClick={onClick}
        dataTestId={`${dataTestId}-preview-button`}
      >
        {trans.SHOW_PREVIEW()}
      </Button>
    ) : (
      <div className={styles.left}>
        <GrabsonIcon name="image" size="lg" />
      </div>
    );

    return (
      <div className={progressClasses.join(" ")}>
        {icon}
        <div className={styles.right}>
          <button
            type="button"
            className={styles.removeButton}
            disabled={status?.removeButtonDisabled || disabled}
            onClick={() => {
              this.removeFile(url, setFieldValue);
            }}
            data-test-id={`${dataTestId}-remove-button`}
          >
            <GrabsonIcon name="close" size="sm" />
          </button>
          <p>{trans.IMAGE_UPLOADED()}</p>
          <Progressbar loaded={loaded} total={100} />
        </div>
      </div>
    );
  }

  fileButton(setFieldValue, setFieldTouched) {
    const { accept, disabled, dataTestId, name } = this.props;
    return (
      <FileButton
        accept={accept}
        onChange={this.uploadFile(setFieldValue, setFieldTouched)}
        disabled={disabled}
        name={name}
        dataTestId={`${dataTestId}-file-button`}
        className={styles.button}
      />
    );
  }

  renderDefault(
    { url, touched, validationError },
    setFieldValue,
    setFieldTouched,
    getFieldProps
  ) {
    const { name, label, required, info, className, dataTestId, tooltip } =
      this.props;
    const imgObj = getFieldProps(name)?.value;
    const disablePreview = this.props.look === "no-preview";

    return (
      <Row className={className || styles.component} dataTestId={dataTestId}>
        <Column>
          {label ? (
            <Label text={label} required={required} tooltip={tooltip} />
          ) : null}
          <div className={styles.row}>
            {imgObj.status?.progressBar &&
              this.progress(
                { url, status: imgObj.status, loaded: imgObj.loaded },
                setFieldValue
              )}
            {imgObj.status?.addButton &&
              this.fileButton(setFieldValue, setFieldTouched)}
            {info && <p className={styles.info}>{info}</p>}
            <ValidationBadge
              error={this.state.error || validationError}
              touched={touched}
            />
          </div>
        </Column>
        {url && !disablePreview && (
          <Column
            className={styles.imgPreviewColumn}
            dataTestId={`${dataTestId}-preview`}
          >
            <Link href={url} target="_blank" rel="nofollow noopener noreferrer">
              <img
                src={url}
                className={styles.imgPreview}
                data-test-id={`${dataTestId}-preview-img`}
              />
            </Link>
          </Column>
        )}
      </Row>
    );
  }

  renderBig({ url, touched, validationError }, setFieldValue, setFieldTouched) {
    const { label, info, required, tooltip } = this.props;

    return (
      <>
        <Label
          text={`${label}${tooltip ? " (?)" : ""}`}
          tooltip={tooltip}
          required={required}
        />
        <img
          src={url || null}
          className={styles.previewImg}
          data-test-id="uploader-preview"
        />
        {info && <p className={styles.info}>{info}</p>}
        <ValidationBadge
          error={this.state.error || validationError}
          touched={touched}
        />
        <ButtonsWrapper className={styles.buttonsBig}>
          {this.fileButton(setFieldValue, setFieldTouched)}
          {touched && (
            <Button
              type="red"
              onClick={() => {
                this.removeFile(url, setFieldValue);
              }}
            >
              {trans.RESET()}
            </Button>
          )}
        </ButtonsWrapper>
      </>
    );
  }

  render() {
    const { name, required } = this.props;
    const urlFieldName = `${name}.url`;
    const validate = createFieldValidator(urlFieldName, isRequired);
    const isBigPreview = this.props.look === "big";

    return (
      <>
        <Field name={urlFieldName} validate={required && validate}>
          {({
            field: { value: url },
            meta: { touched, error: validationError },
            form: { getFieldProps, setFieldValue, setFieldTouched },
          }) => {
            return isBigPreview
              ? this.renderBig(
                  { url, touched, validationError },
                  setFieldValue,
                  setFieldTouched
                )
              : this.renderDefault(
                  { url, touched, validationError },
                  setFieldValue,
                  setFieldTouched,
                  getFieldProps
                );
          }}
        </Field>
      </>
    );
  }
}
